import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, MapPin, Lock } from 'lucide-react';
import { getContent, asset } from '@/lib/api';
import { Seo } from '@/components/Seo';
import { PageHero } from '@/components/PageHero';
import { useMediaQuery } from '@/lib/hooks';
import { LiveProjects } from '@/components/LiveProjects';
import { CtaSection } from '@/components/CtaSection';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const previewRef = useRef(null);
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  useEffect(() => {
    getContent('projects').then(setProjects).catch(() => {});
  }, []);

  const onMove = (e) => {
    if (!previewRef.current) return;
    previewRef.current.style.transform = `translate(${e.clientX + 24}px, ${e.clientY - 120}px)`;
  };

  return (
    <>
      <Seo title='Our Projects' description='Selected earthmoving, excavation, blasting and demolition projects delivered for leading builders and infrastructure clients.' path='/projects' image='/media/img/hero-poster.jpg' />
      <PageHero eyebrow='Our Projects' title='Projects that moved the ground forward.' subtitle='A selection of documented projects across Tamil Nadu and nearby states.' image='/media/img/mine-machinery.jpg' />

      <LiveProjects projects={projects} showAll={false} />

      <section className='relative bg-[#20272A] py-16 lg:py-24' onMouseMove={isDesktop ? onMove : undefined}>
        <div className='grain absolute inset-0' />
        <div className='relative container-x' data-testid='projects-editorial-list'>
          <div className='border-t border-white/10'>
            {projects.filter((p) => !p.ongoing).map((p, i) => {
              if (p.placeholder) {
                return (
                  <div key={p.slug} className='flex items-center gap-6 border-b border-dashed border-white/12 py-7 opacity-70'>
                    <span className='font-mono-num w-10 shrink-0 text-sm text-[#F6F5F1]/40'>{String(i + 1).padStart(2, '0')}</span>
                    <div className='flex-1'>
                      <h3 className='font-head text-2xl font-semibold text-[#F6F5F1]/60'>{p.projectTitle}</h3>
                      <p className='mt-1 text-sm text-[#F6F5F1]/40'>CMS placeholder — fully editable from the Admin Panel.</p>
                    </div>
                    <Lock size={18} className='text-[#F6F5F1]/30' />
                  </div>
                );
              }
              return (
                <Link
                  key={p.slug}
                  to={`/projects/${p.slug}`}
                  data-testid='project-row'
                  data-cursor='VIEW PROJECT'
                  onMouseEnter={() => setActive(p)}
                  onMouseLeave={() => setActive(null)}
                  className='group relative flex items-center gap-6 border-b border-white/10 py-7 transition-colors duration-300 hover:bg-white/[0.03]'
                >
                  <span className='font-mono-num w-10 shrink-0 text-sm text-[#B88A3B]'>{String(i + 1).padStart(2, '0')}</span>
                  <img src={asset(p.heroImage)} alt='' className='h-16 w-24 shrink-0 rounded-lg object-cover lg:hidden' />
                  <div className='min-w-0 flex-1'>
                    <h3 className='truncate font-head text-2xl font-semibold text-[#F6F5F1] transition-colors duration-300 group-hover:text-[#B88A3B] sm:text-3xl'>
                      {p.associatedBrand || p.projectTitle}
                    </h3>
                    <div className='mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#F6F5F1]/55'>
                      <span>{p.client}</span>
                      <span className='hidden text-white/20 sm:inline'>|</span>
                      <span className='inline-flex items-center gap-1.5'><MapPin size={14} className='text-[#B88A3B]' /> {p.location}</span>
                      <span className='hidden text-white/20 sm:inline'>|</span>
                      <span>{(p.serviceCategory || []).join(', ')}</span>
                    </div>
                  </div>
                  <ArrowUpRight size={26} className='hidden shrink-0 text-[#F6F5F1]/40 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#B88A3B] sm:block' />
                </Link>
              );
            })}
          </div>
        </div>

        {isDesktop && (
          <div ref={previewRef} className='pointer-events-none fixed left-0 top-0 z-30 hidden lg:block'>
            <div className='h-56 w-80 overflow-hidden rounded-xl border border-[#B88A3B]/30 shadow-2xl transition-opacity duration-300' style={{ opacity: active ? 1 : 0 }}>
              {active && <img src={asset(active.heroImage)} alt='' className='h-full w-full object-cover' />}
            </div>
          </div>
        )}
      </section>

      <CtaSection />
    </>
  );
}
